import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useRecordLegalAcknowledgementMutation,
  usePrivacyPolicyQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as SecurePaymentLockSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/secure-payment-lock.svg';
import './LegalScreen.scss';

export const PrivacyPolicyScreen: FC = () => {
  const navigate = useNavigate();
  const { data: privacyData } = usePrivacyPolicyQuery();
  const { mutate: recordAck } = useRecordLegalAcknowledgementMutation();

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription');
    }
  };

  const handleContinue = () => {
    recordAck(privacyData?.version ? { privacyVersion: privacyData.version } : undefined);
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/checkout');
    }
  };

  return (
    <div className="legal-page-container">
      <div className="legal-page-header">
        <button type="button" className="legal-back-btn" onClick={handleBack}>
          <UserChevronLeftSVG />
          <span>{'Privacy Policy'}</span>
        </button>
        <p className="legal-header-subtitle">
          {'Learn how miniPT collects, uses, stores, and protects your data.'}
        </p>
      </div>

      <div className="legal-body-wrapper">
        <div className="legal-content-card">
          <div className="legal-section">
            <h3>{'1. Information We Collect'}</h3>
            <p>
              {
                'We collect information you provide directly, such as your name, email address, business details, and payment information when you create an account, manage your subscription, or communicate with us. We also collect automatically certain technical information when you use miniPT, including device, browser, and usage data.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'2. How We Use Your Data'}</h3>
            <p>
              {
                'We use your data to provide, maintain, and improve the miniPT platform, process payments, communicate important updates, deliver customer support, and develop new features. We may also use aggregated and anonymised data for analytics and product improvement.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'3. Billing & Payment Data'}</h3>
            <p>
              {
                'Payment details are processed securely by our trusted payment provider (Stripe). We do not store full card details on our servers. We collect billing information such as name, billing email, address, VAT number, and transaction history to manage your subscription and comply with legal obligations.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'4. Client and Trainer Data'}</h3>
            <p>
              {
                'You may store client information, programs, progress, messages, and notes within miniPT. You are responsible for ensuring you have the appropriate consent from clients to store their data and for keeping that information accurate and up to date.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'5. AI & Uploaded Content'}</h3>
            <p>
              {
                'miniPT includes AI features that may process the information you provide (e.g. program details, notes, messages) to generate suggestions and insights. You remain the owner of your content. Do not upload sensitive personal data you do not wish to be processed.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'6. Data Sharing'}</h3>
            <p>
              {
                'We do not sell your data. We share information only with trusted service providers who help us operate miniPT (e.g. hosting, analytics, email, payments) under strict confidentiality agreements. We may disclose data when required by law or to protect our rights and users.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'7. Data Retention'}</h3>
            <p>
              {
                'We retain your data for as long as your account is active or as needed to provide our services and comply with legal obligations. If you delete your account, we will delete or anonymise your data within 30 days, except where we are required to retain it for legal or accounting reasons.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'8. Security Measures'}</h3>
            <p>
              {
                'We implement industry-standard technical and organisational measures to protect your data, including encryption in transit and at rest, access controls, regular security reviews, and monitoring.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'9. Your Rights'}</h3>
            <p>
              {
                'You have the right to access, update, or delete your personal data, object to or restrict certain processing, and data portability. You can manage your data in-app or contact us to exercise these rights.'
              }
            </p>
          </div>

          <div className="legal-section">
            <h3>{'10. Contact / Support'}</h3>
            <p>
              {
                'If you have any questions about this Privacy Policy or how we handle your data, please contact us.'
              }
            </p>
          </div>
        </div>

        <div className="legal-meta-card">
          <div className="meta-row">
            <span className="meta-label">{'Last updated'}</span>
            <span className="meta-value">{privacyData?.effective_date || '—'}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">{'Version'}</span>
            <span className="meta-value">{privacyData?.version || '—'}</span>
          </div>
        </div>

        <div className="legal-actions-footer">
          <button type="button" className="btn-read-continue" onClick={handleContinue}>
            {'Read & Continue'}
          </button>
          <div className="legal-bottom-disclaimer">
            <SecurePaymentLockSVG />
            <span>{'miniPT is compliant with GDPR. Your data is hosted securely in the EU.'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
